<template>
    <div class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div class="max-w-md w-full space-y-8">
            <!-- Logo Section -->
            <div class="text-center">
                <img 
                    class="mx-auto h-24 w-auto object-contain" 
                    src="../../assets/mainlogo.jpg" 
                    alt="Chapman Xpress Logo"
                />
               
            </div>

            <!-- Login Card -->
            <div class="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <form class="space-y-6" @submit.prevent="login">
                    <!-- Email Input -->
                    <div>
                        <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                        </label>
                        <div class="relative">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <i class="bx bx-envelope text-gray-400 text-xl"></i>
                            </div>
                            <input
                                v-model="form.email"
                                @keydown="clearError"
                                @focus="emailFocused = true"
                                @blur="emailFocused = false"
                                id="email"
                                type="email"
                                required
                                :class="[
                                    'w-full h-12 pl-10 pr-4 border rounded-lg transition-all duration-200',
                                    emailFocused ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300',
                                    isError ? 'border-red-300' : ''
                                ]"
                                placeholder="Enter your email"
                                autocomplete="email"
                            />
                        </div>
                    </div>

                    <!-- Password Input -->
                    <div>
                        <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
                            Password
                        </label>
                        <div class="relative">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <i class="bx bx-lock-alt text-gray-400 text-xl"></i>
                            </div>
                            <input
                                v-model="form.password"
                                @keydown="clearError"
                                @focus="passwordFocused = true"
                                @blur="passwordFocused = false"
                                :type="showPassword ? 'text' : 'password'"
                                id="password"
                                required
                                :class="[
                                    'w-full h-12 pl-10 pr-12 border rounded-lg transition-all duration-200',
                                    passwordFocused ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300',
                                    isError ? 'border-red-300' : ''
                                ]"
                                placeholder="Enter your password"
                                autocomplete="current-password"
                            />
                            <button
                                type="button"
                                @click="togglePasswordVisibility"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <i :class="showPassword ? 'bx bx-hide' : 'bx bx-show'" class="text-xl"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Error Message -->
                    <transition name="fade">
                        <div
                            v-if="isError"
                            class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3"
                        >
                            <i class="bx bx-error-circle text-red-500 text-xl mt-0.5"></i>
                            <div class="flex-1">
                                <p class="text-sm font-medium text-red-800">{{ message }}</p>
                            </div>
                        </div>
                    </transition>

                    <!-- Forgot Password Link -->
                    <div class="flex items-center justify-between">
                       
                    </div>

                    <!-- Submit Button -->
                    <button
                        :disabled="isLoading || !isFormValid"
                        type="submit"
                        :class="[
                            'w-full flex justify-center items-center py-3 px-4 rounded-lg text-white font-medium transition-all duration-200 transform',
                            isLoading || !isFormValid
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:scale-95'
                        ]"
                    >
                        <span v-if="!isLoading" class="flex items-center space-x-2">
                            <span>Sign In</span>
                            <i class="bx bx-right-arrow-alt text-xl"></i>
                        </span>
                        <span v-else class="flex items-center space-x-2">
                            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Signing in...</span>
                        </span>
                    </button>
                </form>

            </div>

            <!-- Additional Info -->
            <div class="text-center">
                <p class="text-sm text-gray-600">
                   Having trouble logging in? contact admin
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import Form from 'vform';
import { mapGetters } from 'vuex';

export default {
    layout: 'login',
    middleware: 'guest',
    components: {},
    data: () => ({
        form: new Form({
            email: '',
            password: '',
        }),
        isLoading: false,
        isError: false,
        message: '',
        showPassword: false,
        emailFocused: false,
        passwordFocused: false,
        rememberMe: false,
    }),

    computed: {
        ...mapGetters({
            user: 'auth/user',
        }),
        isFormValid() {
            return this.form.email && this.form.password;
        },
    },

    methods: {
        togglePasswordVisibility() {
            this.showPassword = !this.showPassword;
        },
        clearError() {
            this.isError = false;
            this.message = '';
        },
        async login() {
            if (!this.isFormValid) return;

            this.isLoading = true;
            this.clearError();

            try {
                const { data } = await this.form.post('/auth/login');
                this.isLoading = false;

                localStorage.token = data.access_token;

                this.$store.dispatch('auth/saveToken', {
                    token: data.access_token,
                    remember: this.rememberMe,
                });
                await this.$store.dispatch('auth/fetchUser');

                if (this.user.data.role === 'teller') {
                    this.$router.push({ path: `/myprofile` });
                } else {
                    this.$router.push({ path: '/dashboard' });
                }
            } catch (error) {
                this.isLoading = false;

                if (error?.response?.status == 401) {
                    this.isError = true;
                    this.message = 'Incorrect email or password. Please try again.';
                } else if (error?.response?.status == 422) {
                    this.isError = true;
                    this.message = 'Please check your input and try again.';
                } else {
                    this.isError = true;
                    this.message = 'An error occurred. Please try again later.';
                }
            }
        },
    },
};
</script>
